import type { QueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { queryOptions } from '@tanstack/react-query'
import { CACHE_CONFIG } from '~/lib/cache-config'
import { COOKIE_NAMES } from '~/lib/app-config'
import { createJwtTokenData, JWT_TOKEN_QUERY_KEY } from '~/lib/jwt-utils'
import { getServerRequest } from '~/lib/server-request'
import { BETTER_AUTH_URL } from '~/utils/auth'

export const SESSION_QUERY_KEY = ['session'] as const

export interface SessionData {
  session: any | null
  user: any | null
  jwtToken?: string
}

async function getSessionFromServer(request: Request): Promise<SessionData> {
  try {
    const headers = new Headers()
    const cookie = request.headers.get('Cookie')
    if (cookie) {
      // 优化：如果 SSR 环境下连认证 Cookie 都不存在，直接短路，省去一次后端 HTTP 请求
      if (!cookie.includes(COOKIE_NAMES.sessionToken)) {
        return { session: null, user: null }
      }
      headers.set('Cookie', cookie)
    } else {
      return { session: null, user: null }
    }

    const userAgent = request.headers.get('User-Agent')
    if (userAgent)
      headers.set('User-Agent', userAgent)

    const response = await fetch(`${BETTER_AUTH_URL}/get-session`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      return { session: null, user: null }
    }

    const data = await response.json()
    const jwtFromHeader = response.headers.get('set-auth-jwt')

    return {
      session: data?.session || null,
      user: data?.user || null,
      jwtToken: jwtFromHeader || undefined,
    }
  }
  catch (error) {
    console.error('--- SSR SESSION FETCH ERROR:', error)
    Sentry.captureException(error, { extra: { source: 'ssr_session' } })
    return { session: null, user: null }
  }
}

export function createSessionQuery(queryClient?: QueryClient) {
  return queryOptions<SessionData>({
    queryKey: SESSION_QUERY_KEY,
    throwOnError: false,
    queryFn: async () => {
      const request = getServerRequest()

      if (request) {
        const sessionData = await getSessionFromServer(request)
        if (sessionData.jwtToken && queryClient) {
          const jwtData = createJwtTokenData(sessionData.jwtToken)
          queryClient.setQueryData([JWT_TOKEN_QUERY_KEY], jwtData)
        }
        return sessionData
      }

      // 客户端路径：直接 fetch 后端接口，绕过 Better Auth SDK 的 cookieCache 黑箱
      // 避免 SDK 乐观返回脏数据导致 TanStack Query 锁定在"已登录"的幽灵状态
      const response = await fetch(`${BETTER_AUTH_URL}/get-session`, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
        cache: 'no-store',
      })

      if (!response.ok) {
        return { session: null, user: null }
      }

      const data = await response.json()
      const jwtFromHeader = response.headers.get('set-auth-jwt')

      if (jwtFromHeader && queryClient) {
        const jwtData = createJwtTokenData(jwtFromHeader)
        queryClient.setQueryData([JWT_TOKEN_QUERY_KEY], jwtData)
      }

      return {
        session: data?.session || null,
        user: data?.user || null,
        jwtToken: jwtFromHeader || undefined,
      }
    },
    staleTime: CACHE_CONFIG.session.staleTime,
    gcTime: CACHE_CONFIG.session.gcTime,
  })
}
